import { Pipe, PipeTransform } from '@angular/core';
import { CollectionMethod, Currency, InvoiceOrigin, InvoiceStatus, LastPaymentStatus } from '@galaxy/billing';
import { BillingFrequency, formatDisplayPrice } from '@vendasta/shared';
import { isPastDue } from './invoice';
import { BadgeColor } from '@vendasta/galaxy/badge';

@Pipe({
  name: 'price',
  standalone: true,
})
export class PricePipe implements PipeTransform {
  transform(
    value: any,
    currency: Currency | string,
    billingFrequency?: BillingFrequency,
    isCents = true,
    translateZero = true,
    displaySymbol = true,
  ): any {
    return formatDisplayPrice(value, currency, billingFrequency, isCents, translateZero, displaySymbol);
  }
}

@Pipe({
  name: 'padInvoiceNumber',
  standalone: true,
})
export class PadInvoiceNumberPipe implements PipeTransform {
  transform(invoiceNumber: number | string): string {
    const invoiceNumberVal = invoiceNumber || 0;
    return `#${invoiceNumberVal.toString().padStart(6, '0')}`;
  }
}

enum InvoiceSources {
  RENEWAL = 'Subscription renewal',
  RECURRING = 'Recurring invoice',
  MANUAL = 'Manual',
}

@Pipe({
  name: 'source',
  pure: true,
  standalone: false,
})
export class InvoiceSourcePipe implements PipeTransform {
  transform(source: string): string {
    switch (source) {
      case InvoiceOrigin.MANUAL:
        return InvoiceSources.MANUAL;
      case InvoiceOrigin.TEMPLATE:
        return InvoiceSources.RECURRING;
      case InvoiceOrigin.RENEWAL:
        return InvoiceSources.RENEWAL;
      default:
        return source;
    }
  }
}

enum InvoiceCollectionMethod {
  EMAIL = 'Send email',
  AUTOMATIC = 'Automatically charge',
  MANUAL = 'Manually collect',
}

@Pipe({
  name: 'collectionMethod',
  pure: true,
  standalone: true,
})
export class InvoiceCollectionMethodPipe implements PipeTransform {
  transform(collectionMethod: string): string {
    switch (collectionMethod) {
      case CollectionMethod.MANUAL_COLLECTION:
        return InvoiceCollectionMethod.MANUAL;
      case CollectionMethod.SEND_EMAIL:
        return InvoiceCollectionMethod.EMAIL;
      case CollectionMethod.CHARGE_AUTOMATICALLY:
        return InvoiceCollectionMethod.AUTOMATIC;
      default:
        return collectionMethod;
    }
  }
}

export interface statusBadgeProps {
  glxyBadgeColor: BadgeColor;
  text: string;
}

@Pipe({
  name: 'statusBadgePipe',
  standalone: true,
})
export class StatusBadgePipe implements PipeTransform {
  private readonly statusProperties: Map<string, statusBadgeProps> = new Map<string, statusBadgeProps>([
    [InvoiceStatus.DRAFT, { text: InvoiceStatus.DRAFT, glxyBadgeColor: 'light-grey' }],
    [InvoiceStatus.VOID, { text: InvoiceStatus.VOID, glxyBadgeColor: 'grey' }],
    [InvoiceStatus.PAID, { text: InvoiceStatus.PAID, glxyBadgeColor: 'green' }],
    [InvoiceStatus.DUE, { text: InvoiceStatus.DUE, glxyBadgeColor: 'blue' }],
    ['Past due', { text: 'Past due', glxyBadgeColor: 'yellow' }],
  ]);

  transform(invoice: any): statusBadgeProps {
    const s = this.statusProperties.get(invoice?.status);
    if (invoice?.status === InvoiceStatus.DUE) {
      return isPastDue(invoice.due) ? this.statusProperties.get('Past due') : s;
    }
    return s;
  }
}

@Pipe({
  standalone: true,
  name: 'paymentStatusBadge',
})
export class PaymentStatusBadgePipe implements PipeTransform {
  transform(status: string): { text: string; color: BadgeColor } {
    switch (status) {
      case LastPaymentStatus.SUCCESS:
        return { text: 'INVOICE.PAYMENT_STATUSES.SUCCEEDED', color: 'blue' };
      case LastPaymentStatus.PENDING:
        return { text: 'INVOICE.PAYMENT_STATUSES.PENDING', color: 'blue' };
      case LastPaymentStatus.FAILED:
        return { text: 'INVOICE.PAYMENT_STATUSES.FAILED', color: 'grey' };
      default:
        return { text: '', color: 'grey' };
    }
  }
}

@Pipe({
  standalone: true,
  name: 'disputeReasonDisplay',
})
export class DisputeReasonDisplayPipe implements PipeTransform {
  transform(reason: string): string {
    switch (reason) {
      case 'bank_cannot_process':
        return 'DISPUTES.DISPUTE_REASON.BANK_CANNOT_PROCESS';
      case 'check_returned':
        return 'DISPUTES.DISPUTE_REASON.CHECK_RETURNED';
      case 'credit_not_processed':
        return 'DISPUTES.DISPUTE_REASON.CREDIT_NOT_PROCESSED';
      case 'customer_initiated':
        return 'DISPUTES.DISPUTE_REASON.CUSTOMER_INITIATED';
      case 'debit_not_authorized':
        return 'DISPUTES.DISPUTE_REASON.DEBIT_NOT_AUTHORIZED';
      case 'duplicate':
        return 'DISPUTES.DISPUTE_REASON.DUPLICATE';
      case 'fraudulent':
        return 'DISPUTES.DISPUTE_REASON.FRAUDULENT';
      case 'general':
        return 'DISPUTES.DISPUTE_REASON.GENERAL';
      case 'incorrect_account_details':
        return 'DISPUTES.DISPUTE_REASON.INCORRECT_ACCOUNT_DETAILS';
      case 'insufficient_funds':
        return 'DISPUTES.DISPUTE_REASON.INSUFFICIENT_FUNDS';
      case 'product_not_received':
        return 'DISPUTES.DISPUTE_REASON.PRODUCT_NOT_RECEIVED';
      case 'product_unacceptable':
        return 'DISPUTES.DISPUTE_REASON.PRODUCT_UNACCEPTABLE';
      case 'subscription_canceled':
        return 'DISPUTES.DISPUTE_REASON.SUBSCRIPTION_CANCELED';
      case 'unrecognized':
        return 'DISPUTES.DISPUTE_REASON.UNRECOGNIZED';
      default:
        return 'DISPUTES.DISPUTE_REASON.UNKNOWN';
    }
  }
}

@Pipe({
  standalone: true,
  name: 'shortCount',
})
export class ShortCountPipe implements PipeTransform {
  transform(value: number): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (value >= 25) {
      return '25+';
    }
    return value.toString();
  }
}
